import { deleteApp, getApps, initializeApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import { getAuth } from 'firebase/auth';

export type FirebaseClientConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
};

/** 既定 app とは別名で持つ。`auth.server.ts` が既定名で初期化しているため衝突させない */
const APP_NAME = 'equtum-web-auth';

let cached: { key: string; auth: Auth } | null = null;

/**
 * ブラウザ側の Firebase Auth インスタンスを取得する。
 *
 * - hook のレンダーごとではなくモジュールスコープで memo する。ログイン画面では
 *   `useGoogleAuth` と `usePasswordAuth` が同時に使われるため、hook ローカルの
 *   `useMemo` だと初期化判定が二重に走る。
 * - サインイン実行時に初めて呼ぶ (lazy)。SSR 中は `initializeApp` / `getAuth` が
 *   一切走らないので、`app/duplicate-app` を構造的に避けられる。
 */
export const getClientAuth = (config: FirebaseClientConfig): Auth => {
  const key = JSON.stringify(config);
  if (cached?.key === key) {
    return cached.auth;
  }

  // config が変わるのは HMR くらいだが、同名 app を options 違いで初期化すると
  // `app/duplicate-app` になるため、作り直す前に破棄する。
  const existing = getApps().find((app) => app.name === APP_NAME);
  if (existing) {
    void deleteApp(existing);
  }

  const auth = getAuth(initializeApp(config, APP_NAME));
  cached = { key, auth };
  return auth;
};
